import apiService from './api';

class AuthService {
  constructor() {
    // Safely initialize from localStorage
    this.token = null;
    this.refreshToken = null;
    this.user = null;
    
    // Get storage keys from environment variables
    this.tokenKey = import.meta.env.VITE_ACCESS_TOKEN_KEY || 'token';
    this.refreshTokenKey = import.meta.env.VITE_REFRESH_TOKEN_KEY || 'refreshToken';
    this.userKey = import.meta.env.VITE_USER_DATA_KEY || 'user';
    
    this.initializeFromStorage();
    this.initializeTokenValidation();
  }

  initializeFromStorage() {
    try {
      const storedToken = localStorage.getItem(this.tokenKey);
      const storedRefreshToken = localStorage.getItem(this.refreshTokenKey);
      const storedUser = localStorage.getItem(this.userKey);
      
      if (storedToken) {
        this.token = storedToken;
        this.refreshToken = storedRefreshToken;
        
        // Safely parse user data
        if (storedUser && storedUser !== 'undefined' && storedUser !== 'null') {
          try {
            this.user = JSON.parse(storedUser);
          } catch (parseError) {
            console.error('Failed to parse stored user data:', parseError);
            this.clearInvalidData();
            return;
          }
        }
        
        // Ensure API service has the same token
        apiService.setToken(storedToken);
      }
    } catch (error) {
      console.error('Failed to initialize from storage:', error);
      this.clearInvalidData();
    }
  }

  clearInvalidData() {
    this.token = null;
    this.refreshToken = null;
    this.user = null;
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.userKey);
  }

  initializeTokenValidation() {
    // Listen for token invalidation events
    window.addEventListener('authTokenInvalid', () => {
      console.log('🔓 Token invalidated, logging out (no reload)');
      this.logout();
      // Don't reload - let AuthContext and ProtectedRoute handle the redirect
      // Reloading causes infinite loop when token validation repeatedly fails
    });
  }

  async register(userData) {
    try {
      console.log('Registering user:', userData);
      
      const data = await apiService.register(userData);
      console.log('Register response data:', data);

      this.setAuth(data.access_token, data.refresh_token, data.user);
      apiService.setToken(data.access_token);
      return { success: true, user: data.user };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: error.message };
    }
  }

  async login(credentials) {
    try {
      console.log('Logging in user:', credentials);
      
      const data = await apiService.login(credentials);
      console.log('Login response data:', data);

      this.setAuth(data.access_token, data.refresh_token, data.user);
      apiService.setToken(data.access_token);
      return { success: true, user: data.user };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  }


  async getProfile() {
    if (!this.token) return null;

    try {
      const data = await apiService.getProfile();
      // Update user data if profile fetch is successful
      this.user = data.user;
      localStorage.setItem(this.userKey, JSON.stringify(data.user));
      return data.user;
    } catch (error) {
      console.error('Profile fetch error:', error);
      this.logout();
      return null;
    }
  }

  async refreshTokens() {
    if (!this.refreshToken) {
      console.log('🔍 No refresh token found');
      return false;
    }

    try {
      console.log('🔄 Refreshing tokens...');
      const data = await apiService.refreshTokens(this.refreshToken);
      
      this.setAuth(data.access_token, data.refresh_token, data.user);
      apiService.setToken(data.access_token);
      
      console.log('✅ Tokens refreshed successfully');
      return true;
    } catch (error) {
      console.error('❌ Token refresh failed:', error);
      this.clearInvalidData();
      return false;
    }
  }

  async validateToken() {
    if (!this.token) {
      console.log('🔍 No token found for validation');
      return false;
    }

    try {
      console.log('🔍 Validating token...');
      const user = await this.getProfile();
      
      if (user) {
        console.log('✅ Token validation successful:', { userName: user.name });
        return true;
      } else {
        console.log('❌ Token validation failed: No user returned');
        this.clearInvalidData();
        return false;
      }
    } catch (error) {
      console.error('❌ Token validation error:', error);
      
      // Try to refresh token if it's an authentication error (401)
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        console.log('🔄 Access token expired, trying to refresh...');
        const refreshSuccess = await this.refreshTokens();
        
        if (refreshSuccess) {
          console.log('✅ Token refreshed, retrying validation');
          return this.validateToken();
        } else {
          console.log('🔓 Refresh failed, clearing auth data');
          this.clearInvalidData();
          return false;
        }
      } else {
        console.log('⚠️ Network or server error, keeping token');
        return false;
      }
    }
  }

  setAuth(token, refreshToken, user) {
    try {
      this.token = token;
      this.refreshToken = refreshToken;
      this.user = user;
      
      // Store in localStorage with error handling
      localStorage.setItem(this.tokenKey, token);
      if (refreshToken) {
        localStorage.setItem(this.refreshTokenKey, refreshToken);
      }
      localStorage.setItem(this.userKey, JSON.stringify(user));
      
      // Synchronize with API service
      apiService.setToken(token);
      
      console.log('✅ Auth data saved successfully:', { 
        tokenExists: !!token, 
        refreshTokenExists: !!refreshToken,
        userExists: !!user,
        userName: user?.name 
      });
    } catch (error) {
      console.error('Failed to set auth data:', error);
      throw new Error('Failed to save authentication data');
    }
  }

  logout() {
    try {
      this.token = null;
      this.refreshToken = null;
      this.user = null;
      
      // Clear localStorage
      localStorage.removeItem(this.tokenKey);
      localStorage.removeItem(this.refreshTokenKey);
      localStorage.removeItem(this.userKey);
      
      // Clear API service token
      apiService.clearAuth();
      
      console.log('✅ Logout completed successfully');
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }

  isAuthenticated() {
    return !!this.token && !!this.user;
  }

  getToken() {
    return this.token;
  }

  getUser() {
    return this.user;
  }
}

export default new AuthService();